import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export interface OnlineUser {
  userId: number
  name: string
}

export type ConnectionState = 'live' | 'reconnecting'

interface ServerMessage {
  type: 'hello' | 'change' | 'presence' | 'resync'
  userId?: number
  entity?: string
  action?: string
  id?: number
  actorId?: number | null
  /** 'mcp' when an agent made the change, whoever's token it used. */
  source?: 'app' | 'mcp'
  online?: OnlineUser[]
}

interface RealtimeValue {
  online: OnlineUser[]
  connection: ConnectionState
  /** Keys of things another actor changed in the last 1.6 s — drives the enter flash. */
  remoteChanges: ReadonlySet<string>
}

const RealtimeContext = createContext<RealtimeValue>({
  online: [],
  connection: 'live',
  remoteChanges: new Set(),
})

const FLASH_MS = 1600
const BACKOFF_MIN_MS = 500
const BACKOFF_MAX_MS = 15_000
/** Badge only after a second of silence — a blink on every reload would be noise (ui-spec §6). */
const BADGE_DELAY_MS = 1000

function changeKey(entity: string, id: number): string {
  return `${entity}:${id}`
}

/**
 * One socket for the whole app, mounted in the shell. Two would mean double invalidation and double
 * presence.
 *
 * Events do not carry data: they say what changed, and the client refetches (ADR 0004). That is
 * cheaper and safer at this scale than patching the cache by hand, which RULES §3 forbids outright.
 */
export function RealtimeProvider({
  meId,
  children,
}: {
  meId: number
  children: ReactNode
}): React.JSX.Element {
  const queryClient = useQueryClient()
  const [online, setOnline] = useState<OnlineUser[]>([])
  const [connection, setConnection] = useState<ConnectionState>('live')
  const [remoteChanges, setRemoteChanges] = useState<ReadonlySet<string>>(new Set())

  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const meIdRef = useRef(meId)
  meIdRef.current = meId

  const markRemote = useCallback((key: string) => {
    setRemoteChanges((previous) => new Set(previous).add(key))

    const existing = timers.current.get(key)
    if (existing) clearTimeout(existing)

    timers.current.set(
      key,
      setTimeout(() => {
        timers.current.delete(key)
        setRemoteChanges((previous) => {
          const next = new Set(previous)
          next.delete(key)
          return next
        })
      }, FLASH_MS),
    )
  }, [])

  useEffect(() => {
    let socket: WebSocket | null = null
    let closed = false
    let backoff = BACKOFF_MIN_MS
    let retry: ReturnType<typeof setTimeout> | null = null
    let badgeTimer: ReturnType<typeof setTimeout> | null = null
    const pending = timers.current

    const invalidate = (entity: string): void => {
      if (entity === 'tasks' || entity === 'task_comments') {
        void queryClient.invalidateQueries({ queryKey: ['tasks'] })
        void queryClient.invalidateQueries({ queryKey: ['task'] })
      }
      // The thread has its own key. Without this the card's counter would update while the open
      // discussion sat there stale — which is exactly the thing stage 5 exists to deliver.
      if (entity === 'task_comments') {
        void queryClient.invalidateQueries({ queryKey: ['comments'] })
      }
      if (entity === 'columns') void queryClient.invalidateQueries({ queryKey: ['columns'] })
      if (entity === 'notes') void queryClient.invalidateQueries({ queryKey: ['notes'] })
      // Jedno powiązanie siedzi w dwóch listach naraz — notatkach zadania i zadaniach notatki —
      // a zdarzenie niesie id wiersza łączącego, nie id żadnej ze stron (ADR 0012).
      if (entity === 'task_notes') {
        void queryClient.invalidateQueries({ queryKey: ['task-notes'] })
        void queryClient.invalidateQueries({ queryKey: ['note-tasks'] })
      }
      // Dzwonek zapala się tym samym kanałem co reszta — bez odpytywania (ADR 0004).
      if (entity === 'notifications') {
        void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      }
    }

    const connect = (): void => {
      if (closed) return

      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      socket = new WebSocket(`${proto}://${window.location.host}/ws`)

      socket.addEventListener('open', () => {
        backoff = BACKOFF_MIN_MS
        if (badgeTimer) {
          clearTimeout(badgeTimer)
          badgeTimer = null
        }
        setConnection('live')
      })

      socket.addEventListener('message', (event) => {
        let message: ServerMessage
        try {
          message = JSON.parse(String(event.data)) as ServerMessage
        } catch {
          return
        }

        if (message.type === 'presence') {
          setOnline(message.online ?? [])
          return
        }

        if (message.type === 'resync') {
          // Notifications sent while the server's LISTEN was down are gone for good.
          void queryClient.invalidateQueries()
          return
        }

        if (message.type === 'change' && message.entity && typeof message.id === 'number') {
          invalidate(message.entity)
          // Flash what arrived "from nowhere", never our own clicking (ui-spec §6). An agent counts
          // as nowhere even on its owner's screen: it runs on your token, but you did not do it,
          // and a card appearing unannounced is precisely what the highlight is for (PRD §4.4).
          const mine = message.actorId === meIdRef.current && message.source !== 'mcp'
          if (message.actorId !== null && !mine) {
            markRemote(changeKey(message.entity, message.id))
          }
        }
      })

      const onGone = (): void => {
        if (closed) return
        socket = null
        if (!badgeTimer)
          badgeTimer = setTimeout(() => setConnection('reconnecting'), BADGE_DELAY_MS)
        const delay = backoff
        backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
        retry = setTimeout(connect, delay)
      }

      socket.addEventListener('close', onGone)
      socket.addEventListener('error', () => socket?.close())
    }

    connect()

    return () => {
      closed = true
      if (retry) clearTimeout(retry)
      if (badgeTimer) clearTimeout(badgeTimer)
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()

      // Closing a socket that is still CONNECTING logs a warning in every browser. StrictMode
      // mounts effects twice in dev, so without this the console fills with noise on every reload.
      if (socket?.readyState === WebSocket.CONNECTING) {
        socket.addEventListener('open', function closeOnOpen() {
          this.close()
        })
      } else {
        socket?.close()
      }
    }
  }, [queryClient, markRemote])

  const value = useMemo(
    () => ({ online, connection, remoteChanges }),
    [online, connection, remoteChanges],
  )

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
}

export function useRealtime(): RealtimeValue {
  return useContext(RealtimeContext)
}

/** True for 1.6 s after somebody else changed this row. Drives EnterHighlight. */
export function useRemoteChange(entity: string, id: number): boolean {
  const { remoteChanges } = useRealtime()
  return remoteChanges.has(changeKey(entity, id))
}

/** Optimistic updates are a lie while the socket is down (RULES §3). */
export function useIsLive(): boolean {
  return useRealtime().connection === 'live'
}
